import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Alert, Skeleton, Box } from '@mui/material';

import HeaderSection from './components/HeaderSection';
import StatsCards from './components/StatsCards';
import DifferedValueCard from './components/DifferedValueCard';
import ActionCards from './components/ActionCards';

import ArchitectureIcon from '@mui/icons-material/Architecture';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileCopyIcon from '@mui/icons-material/FileCopy';

import { fetchProjectById } from './services/projectDashboardService';

import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import { getDashboardStats, COMPACT_THRESHOLD } from '../../../config/statusDisplayResolver';
import { canSeeDeferredCost, isTradeRole } from '../../../utils/roles';

const ProjectDashboard = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { canProject, getProjectRole, isLoading: permissionsLoading } = usePermissions();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [project, setProject] = useState(null);

    const role = !permissionsLoading ? getProjectRole(projectId) : null;
    const canSeeReports = !permissionsLoading && canProject(projectId, PERMISSIONS.REPORTS_READ);
    const canSeeCloseoutDocs =
        !permissionsLoading && canProject(projectId, PERMISSIONS.CLOSEOUT_DOCS_READ);
    const showDeferredValue = role && canSeeDeferredCost(role);
    const canSeeSettings = role && !isTradeRole(role);

    const projectActions = useMemo(() => {
        if (permissionsLoading) return [];

        const actions = [
            {
                id: 'drawings',
                label: 'Drawings',
                icon: <ArchitectureIcon fontSize="medium" />,
            },
            {
                id: 'deficiencies',
                label: 'Deficiencies',
                icon: <AssignmentTurnedInIcon fontSize="medium" />,
            },
        ];

        if (canSeeReports) {
            actions.push({
                id: 'reports',
                label: 'Reports',
                icon: <BarChartIcon fontSize="medium" />,
            });
        }

        if (canSeeCloseoutDocs) {
            actions.push({
                id: 'closeoutDoc',
                label: 'Closeout Docs',
                icon: <FileCopyIcon fontSize="medium" />,
            });
        }

        return actions;
    }, [permissionsLoading, canSeeReports, canSeeCloseoutDocs]);

    const stats = useMemo(() => {
        if (!project || !role) return [];
        return getDashboardStats(role, project.deficiencyCounts);
    }, [project, role]);

    const useCompactStats = stats.length > 0 && stats.length < COMPACT_THRESHOLD;

    useEffect(() => {
        const loadProject = async () => {
            try {
                setLoading(true);
                const projectData = await fetchProjectById(projectId);
                setProject(projectData);

                if (projectData && projectData.name) {
                    sessionStorage.setItem(`project_${projectId}_name`, projectData.name);
                }

                setError(null);
            } catch (err) {
                console.error('Error loading project:', err);
                setError('Failed to load project data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        loadProject();
    }, [projectId]);

    const handleBack = () => {
        navigate('/projects');
    };

    const handleStatClick = (stat) => {
        if (stat?.label) {
            navigate(`/projects/${projectId}/deficiencies`, {
                state: { filterByStatus: stat.label },
            });
        }
    };

    const handleFeatureClick = (feature) => {
        switch (feature) {
            case 'drawings':
                navigate(`/projects/${projectId}/plans`);
                break;
            case 'deficiencies':
                navigate(`/projects/${projectId}/deficiencies`);
                break;
            case 'reports':
                navigate(`/projects/${projectId}/reports`);
                break;
            case 'closeoutDoc':
                navigate(`/projects/${projectId}/closeout-docs`);
                break;
            default:
                break;
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} sx={{ color: '#1652f0' }} />
                </Box>
            ) : error ? (
                <Box sx={{ my: 2 }}>
                    <Alert severity="error" sx={{ borderRadius: '8px' }}>
                        {error}
                    </Alert>
                </Box>
            ) : (
                <Box>
                    <HeaderSection
                        projectName={project.name}
                        fieldId={project.fieldId}
                        onBack={handleBack}
                        showSettings={canSeeSettings}
                    />

                    <Box
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                            border: '1px solid #f0f0f0',
                            mb: 3,
                        }}
                    >
                        <StatsCards
                            stats={stats}
                            onStatClick={handleStatClick}
                            deferredValue={
                                useCompactStats && showDeferredValue
                                    ? project.differedValue
                                    : undefined
                            }
                        />

                        {showDeferredValue && !useCompactStats && (
                            <DifferedValueCard value={project.differedValue} />
                        )}

                        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                            {permissionsLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: { xs: 2, sm: 4 },
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {[1, 2, 3, 4].map((i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rounded"
                                            width={140}
                                            height={90}
                                            sx={{ borderRadius: '12px', flexShrink: 0 }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <ActionCards
                                    actions={projectActions}
                                    onActionClick={handleFeatureClick}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ProjectDashboard;
